import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User, Mail, Building, FileText, Phone, MapPin, Lock, Save, ArrowLeft, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { handleMobileInput, handleGSTInput, validateMobile, validateGST } from '@/utils/inputValidation';
import { PasswordStrengthChecklist, validatePasswordPolicy } from '@/components/PasswordStrengthChecklist';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [profileData, setProfileData] = useState({
    email: '',
    company_name: '',
    gst_number: '',
    phone: '',
    address: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchProfile();
    if (user?.role === 'reseller' || user?.role === 'sub_reseller') {
      fetchLogoSettings();
    }
  }, [user]);

  const fetchLogoSettings = async () => {
    try {
      const response = await axios.get(`${API}/white-label/settings`);
      if (response.data?.logo_url) {
        setLogoUrl(response.data.logo_url);
      }
    } catch (error) {
      // Settings might not exist, that's okay
    }
  };

  const getLogoDisplayUrl = (url) => {
    if (!url) return null;
    return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PNG, JPG, GIF, WEBP');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/white-label/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploaded successfully! Refresh page to see changes.');
      setLogoUrl(response.data.logo_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/white-label/delete-logo`);
      toast.success('Logo removed successfully');
      setLogoUrl(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete logo');
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setProfileData({
        email: response.data.email || '',
        company_name: response.data.company_name || '',
        gst_number: response.data.gst_number || '',
        phone: response.data.phone || '',
        address: response.data.address || ''
      });
    } catch (error) {
      toast.error('Failed to fetch profile data');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isClient = user?.role === 'client';

  const handleProfileChange = (field, value) => {
    // Clients may only edit company_name and address
    if (isClient && field !== 'company_name' && field !== 'address') return;
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Clients: send only the two editable fields
      const payload = isClient
        ? { company_name: profileData.company_name, address: profileData.address }
        : { email: profileData.email, company_name: profileData.company_name, gst_number: profileData.gst_number, phone: profileData.phone, address: profileData.address };
      await axios.put(`${API}/profile`, payload);
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (!validatePasswordPolicy(passwordData.new_password)) {
      toast.error('Password does not meet requirements');
      return;
    }
    setChangingPassword(true);

    try {
      await axios.put(`${API}/profile/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getBackPath = () => {
    if (user?.role === 'client') return '/client/dashboard';
    return '/reseller/dashboard';
  };

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(getBackPath())}
            data-testid="back-button"
            className="bg-blue-600 text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="mb-3">
            <h1 className="text-3xl font-bold tracking-tight" data-testid="profile-title">Edit Profile</h1>
            <p className="text-slate-500 mt-1">Manage your account settings</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Profile Information Card */}
            <Card data-testid="profile-info-card" className="mb-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Profile Information
                </CardTitle>
                <CardDescription>Update your personal and business details</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveProfile} className="space-y-4">

                  {/* Email — always locked for clients */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2 !mb-1">
                      <Mail className="h-4 w-4" />
                      Email Address
                    </Label>
                    {isClient ? (
                      <div className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-slate-100 text-sm text-slate-500 cursor-not-allowed select-none flex items-center gap-2" data-testid="profile-email">
                        <Lock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        {profileData.email}
                      </div>
                    ) : (
                      <Input id="email" type="email" value={profileData.email}
                        onChange={(e) => handleProfileChange('email', e.target.value)}
                        placeholder="Enter your email" data-testid="profile-email" />
                    )}
                  </div>

                  {/* Company Name — editable for all */}
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      Company Name
                    </Label>
                    <Input id="company_name" type="text" value={profileData.company_name}
                      onChange={(e) => handleProfileChange('company_name', e.target.value)}
                      placeholder="Enter company name" data-testid="profile-company" />
                  </div>

                  {/* GST Number — locked for clients */}
                  <div className="space-y-2">
                    <Label htmlFor="gst_number" className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4" />
                      GST Number
                    </Label>
                    {isClient ? (
                      <div className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-slate-100 text-sm text-slate-500 cursor-not-allowed select-none font-mono flex items-center gap-2" data-testid="profile-gst">
                        <Lock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        {profileData.gst_number || '—'}
                      </div>
                    ) : (
                      <Input id="gst_number" type="text" value={profileData.gst_number}
                        onChange={(e) => handleProfileChange('gst_number', handleGSTInput(e.target.value))}
                        placeholder="Enter GST number" data-testid="profile-gst" />
                    )}
                  </div>

                  {/* Phone — locked for clients */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2 mb-1">
                      <Phone className="h-4 w-4" />
                      Phone Number
                    </Label>
                    {isClient ? (
                      <div className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-slate-100 text-sm text-slate-500 cursor-not-allowed select-none flex items-center gap-2" data-testid="profile-phone">
                        <Lock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        {profileData.phone || '—'}
                      </div>
                    ) : (
                      <Input id="phone" type="tel" value={profileData.phone}
                        onChange={(e) => handleProfileChange('phone', handleMobileInput(e.target.value))}
                        placeholder="Enter phone number" data-testid="profile-phone" />
                    )}
                  </div>

                  {/* Address — editable for all */}
                  <div className="space-y-2">
                    <Label htmlFor="address" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Address
                    </Label>
                    <Input id="address" type="text" value={profileData.address}
                      onChange={(e) => handleProfileChange('address', e.target.value)}
                      placeholder="Enter address" data-testid="profile-address" />
                  </div>

                  <Button type="submit" disabled={saving} className="w-full" data-testid="save-profile-btn">
                    {saving ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Saving...</>
                    ) : (
                      <><Save className="h-4 w-4 mr-2" />Save Profile</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Company Logo Card - Only for Partners */}
            {(user?.role === 'reseller' || user?.role === 'sub_reseller') && (
              <Card data-testid="logo-card" className="mb-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5 text-blue-600" />
                    Company Logo
                  </CardTitle>
                  <CardDescription>Upload your company logo to display in dashboard header</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6">
                    {logoUrl ? (
                      <div className="flex items-center gap-6">
                        <div className="w-24 h-24 bg-slate-50 rounded-xl overflow-hidden flex items-center justify-center border border-slate-200">
                          <img 
                            src={getLogoDisplayUrl(logoUrl)} 
                            alt="Company logo" 
                            className="w-20 h-20 object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        <div className="flex-1 space-y-3">
                          <p className="text-sm font-medium text-slate-700">Your company logo is set</p>
                          <p className="text-xs text-slate-500">This logo will appear in your dashboard header</p>
                          <div className="flex gap-2">
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}
                              data-testid="change-logo-btn"
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              Change Logo
                            </Button>
                            <Button 
                              type="button" 
                              variant="destructive" 
                              size="sm"
                              onClick={handleDeleteLogo}
                              data-testid="delete-logo-btn"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="flex flex-col items-center justify-center py-8 cursor-pointer hover:bg-slate-50 rounded-lg transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                          <ImageIcon className="h-10 w-10 text-slate-400" />
                        </div>
                        <p className="text-base font-medium text-slate-700">Upload your company logo</p>
                        <p className="text-sm text-slate-500 mt-1">PNG, JPG, GIF up to 2MB</p>
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="mt-4"
                          disabled={uploading}
                          data-testid="upload-logo-btn"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploading ? 'Uploading...' : 'Select File'}
                        </Button>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-file-input"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Change Password Card */}
            <Card data-testid="password-card" className="mb-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-blue-600" />
                  Change Password
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                      placeholder="Enter current password"
                      data-testid="current-password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                      placeholder="Min 8 chars, upper, lower, number, special"
                      data-testid="new-password"
                    />
                    <PasswordStrengthChecklist password={passwordData.new_password} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                      placeholder="Confirm new password"
                      data-testid="confirm-password"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    variant="outline"
                    disabled={changingPassword || !passwordData.current_password || !passwordData.new_password}
                    className="w-full"
                    data-testid="change-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Changing...
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
